
import { AtlasKitThemeProvider } from '@atlaskit/theme';
import React, { Component } from 'react';

import VideoLayout from '../../../../../modules/UI/videolayout/VideoLayout';
import { normalizeAudioLevel } from '../../../audio-level-indicator/functions';
import { Avatar } from '../../../base/avatar';
import { setCustomParticipantVisible } from '../../../base/conference';
import { getActiveDOMDocument, getPictureInPictureModeConfig } from '../../../base/config/functions.any';
import { translate } from '../../../base/i18n';
import { Icon, IconClose, IconModeratorPin, IconNetwork, IconUserPinned } from '../../../base/icons';
import MeetHourJS from '../../../base/lib-meet-hour/_';
import { MEDIA_TYPE, VideoTrack } from '../../../base/media';
import AudioTrack from '../../../base/media/components/web/AudioTrack';
import {
    getLocalParticipant,
    getParticipantById,
    getParticipantCount,
    getScreenSharingParticipantId,
    getScreenshareParticipantIds,
    getVirtualScreenshareParticipantOwnerId,
    participantVolumeChanged
} from '../../../base/participants';
import { BaseIndicator } from '../../../base/react';
import { connect } from '../../../base/redux';
import {
    getLocalAudioTrack,
    getLocalDesktopTrack,
    getLocalVideoTrack,
    getScreenShareTrack,
    getTrackByMediaTypeAndParticipant,
    getVirtualScreenshareParticipantTrack,
    isLocalTrackMuted,
    isRemoteTrackMuted
} from '../../../base/tracks';
import { ConnectionIndicator } from '../../../connection-indicator';
import ConnectionLostOverlay from '../../../connection-indicator/components/web/ConnectionLostOverlay';
import { DisplayName } from '../../../display-name';
import {
    DominantSpeakerIndicator,
    StatusIndicators
} from '../../../filmstrip';
import {
    getGifDisplayMode,
    getGifForParticipant
} from '../../../gifs/function.any';
import { PresenceLabel } from '../../../presence-status';
import {
    LAYOUTS,
    getCurrentLayout,
    shouldDisplayTileView
} from '../../../video-layout';

import RemoteVideoMenu from './RemoteVideoMenu';
import ThumbnailVideoBackground from './ThumbnailVideoBackground';

declare var config: Object;

const MHTrackEvents = MeetHourJS.events.track;

/* global interfaceConfig */

/**
 * The type of the React {@code Component} state of {@link Thumbnail}.
 */
type State = {

    /**
     * The current audio level value for the Thumbnail.
     */
    audioLevel: number,

    /**
     * The current volume setting for the Thumbnail.
     */
    volume: ?number,
};

/**
 * The type of the React {@code Component} props of {@link Thumbnail}.
 */
type Props = {

    /**
     * The audio track related to the participant.
     */
    _audioTrack: ?Object,

    /**
     * Whether the participant is audio muted.
     */
    _isAudioMuted: boolean,

    /**
     * Disable/enable the auto hide functionality for the connection indicator.
     */
    _connectionIndicatorAutoHideEnabled: boolean,

    /**
     * Disable/enable the connection indicator.
     */
    _connectionIndicatorDisabled: boolean,

    /**
     * The current layout of the filmstrip.
     */
    _currentLayout: string,

    /**
     * The default display name for the local participant.
     */
    _defaultLocalDisplayName: string,

    /**
     * Indicates whether the profile functionality is disabled.
     */
    _disableProfile: boolean,

    /**
     * The height of the Thumbnail.
     */
    _height: number,

    /**
     * The aspect ratio of the Thumbnail in percents.
     */
    _heightToWidthPercent: number,

    /**
     * Disable/enable the dominant speaker indicator.
     */
    _isDominantSpeakerDisabled: boolean,

    /**
     * The size of the icon of indicators.
     */
    _indicatorIconSize: number,

    /**
     * An object with information about the participant related to the thumbnaul.
     */
    _participant: Object,

    /**
     * The number of participants in the call.
     */
    _participantCount: number,

    /**
     * Indicates whether the "start silent" mode is enabled.
     */
    _startSilent: Boolean,

    /**
     * Whether local video mirror is enabled.
     */
    _localFlipX: boolean,

    /**
     * The objectFit setting from Redux state.
     */
    _objectFit: string,

    /**
     * The video track that will be displayed in the thumbnail.
     */
    _videoTrack: ?Object,

    /**
     * The width of the thumbnail.
     */
    _width: number,

    /**
     * The redux dispatch function.
     */
    dispatch: Function,

    /**
     * Indicates whether the thumbnail is hovered or not.
     */
    isHovered: ?boolean,

    /**
     * The ID of the participant related to the thumbnail.
     */
    participantID: ?string,
};

/**
 * Implements a thumbnail.
 *
 * @augments Component
 */
class Thumbnail extends Component<Props, State> {
    /**
   * Initializes a new Thumbnail instance.
   *
   * @param {Object} props - The read-only React Component props with which
   * the new instance is to be initialized.
   */
    constructor(props: Props) {
        super(props);

        this.state = {
            audioLevel: 0,
            volume: undefined
        };

        this._updateAudioLevel = this._updateAudioLevel.bind(this);
        this._onVolumeChange = this._onVolumeChange.bind(this);
        this._onInitialVolumeSet = this._onInitialVolumeSet.bind(this);
    }

    /**
   * Starts listening for audio level updates after the initial render.
   *
   * @inheritdoc
   * @returns {void}
   */
    componentDidMount() {
        this._listenForAudioUpdates();
    }

    /**
   * Stops listening for audio level updates on the old track and starts
   * listening instead on the new track.
   *
   * @inheritdoc
   * @returns {void}
   */
    componentDidUpdate(prevProps: Props) {
        if (prevProps._audioTrack !== this.props._audioTrack) {
            this._stopListeningForAudioUpdates(prevProps._audioTrack);
            this._listenForAudioUpdates();
            this._updateAudioLevel(0);
        } else if (prevProps._isAudioMuted !== this.props._isAudioMuted && this.props._isAudioMuted) {
            this._updateAudioLevel(0);
        }

        // Synchronize volume changes everywhere using participantVolumeChanged
        if (prevProps._participant?.volume !== this.props._participant?.volume || this.state.volume !== this.props._participant?.volume) {
            participantVolumeChanged(this.props.participantID, this.state.volume);
            this.setState({ volume: this.props._participant?.volume });
        }

        if (
            prevProps._videoTrack?.muted !== this.props._videoTrack?.muted
            || this.props._videoTrack?.muted === undefined
        ) {
            const state = APP.store.getState();
            const activeWindow = getActiveDOMDocument(state);
            const localVideo = activeWindow.getElementById('localVideoContainer');

            if (localVideo) {
                if (this.props._videoTrack) {
                    VideoLayout.refreshLayout?.();
                }
            }
        }
    }

    /**
   * Unsubscribe from audio level updates.
   *
   * @inheritdoc
   * @returns {void}
   */
    componentWillUnmount() {
        this._stopListeningForAudioUpdates(this.props._audioTrack);
    }

    /**
   * Starts listening for audio level updates from the library.
   *
   * @private
   * @returns {void}
   */
    _listenForAudioUpdates() {
        const { _audioTrack } = this.props;

        if (_audioTrack) {
            const { mhTrack } = _audioTrack;

            mhTrack
                && mhTrack.on(
                    MHTrackEvents.TRACK_AUDIO_LEVEL_CHANGED,
                    this._updateAudioLevel
                );
        }
    }

    /**
   * Stops listening to further updates from the passed track.
   *
   * @param {Object} audioTrack - The track.
   * @private
   * @returns {void}
   */
    _stopListeningForAudioUpdates(audioTrack) {
        if (audioTrack) {
            const { mhTrack } = audioTrack;

            mhTrack
                && mhTrack.off(
                    MHTrackEvents.TRACK_AUDIO_LEVEL_CHANGED,
                    this._updateAudioLevel
                );
        }
    }

    /**
     * Handles volume change from VolumeSlider or RemoteVideo and synchronizes everywhere.
     * @param {number} newVolume
     */
    _onVolumeChange = newVolume => {
        this.setState({ volume: newVolume });
        participantVolumeChanged(this.props.participantID, newVolume);

        // Optionally, dispatch redux action if needed
        // this.props.dispatch(updateParticipantVolume(this.props.participantID, newVolume));
    };

    /**
     * Updates the internal state of the last know audio level. The level should
     * be between 0 and 1, as the level will be used as a percentage out of 1.
     *
     * @param {number} audioLevel - The new audio level for the track.
     * @private
     * @returns {void}
     */
    _updateAudioLevel(audioLevel) {
        const level = this.props._isAudioMuted || this.props._audioTrack?.muted
            ? 0 : normalizeAudioLevel(audioLevel);

        // Levels arrive several times a second per participant, and a microphone's background noise alone keeps
        // changing the raw value. Each setState re-renders the whole tile, so only update when what is drawn changes.
        if (level !== this.state.audioLevel) {
            this.setState({
                audioLevel: level
            });
        }
    }

    /**
   * Returns an object with the styles for thumbnail.
   *
   * @returns {Object} - The styles for the thumbnail.
   */
    _getStyles(): Object {
        const { _height, _currentLayout, _isAudioMuted, _audioTrack } = this.props;
        let styles;
        const currentAudioLevel = _isAudioMuted || _audioTrack?.muted ? 0 : this.state.audioLevel;
        const boxShadow = this._updateSmallVideoShadow(currentAudioLevel);

        switch (_currentLayout) {
        case LAYOUTS.TILE_VIEW: {
            styles = {
                avatarContainer: {
                    height: '50px',
                    width: '50px',
                    boxShadow
                }
            };
            break;
        }
        case LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW: {
            const avatarSize = _height / 2;

            styles = {
                avatarContainer: {
                    height: `${avatarSize}px`,
                    width: `${avatarSize}px`,
                    boxShadow
                }
            };
            break;
        }

        // flimstrip local avatar
        case LAYOUTS.VERTICAL_FILMSTRIP_VIEW: {
            styles = {
                avatarContainer: {
                    height: '50px',
                    width: '50px',
                    boxShadow
                }
            };
            break;
        }
        case LAYOUTS.STAGE_VIEW: {
            styles = {
                avatarContainer: {
                    height: '50px',
                    width: '50px',
                    boxShadow
                }
            };
            break;
        }
        default: {
            // Fallback styles for any unhandled layout
            styles = {
                avatarContainer: {
                    height: '50px',
                    width: '50px',
                    boxShadow
                }
            };
            break;
        }
        }

        return styles;
    }

    /**
   * Renders a fake participant (youtube video or screenshare) thumbnail.
   *
   * @param {string} id - The id of the participant.
   * @returns {ReactElement}
   */
    _renderFakeParticipant() {
        const { _participant, _videoTrack } = this.props;
        const { id, isFakeParticipant } = _participant;

        // Handle screenshare fake participants
        if (isFakeParticipant === 'LocalScreenShare' || isFakeParticipant === 'RemoteScreenShare') {
            const styles = this._getStyles();

            return (
                <>
                    <span
                        className = 'videocontainer'
                        style = { styles }>
                        <VideoTrack
                            id = { `track_${id}` }
                            videoTrack = { _videoTrack } />
                    </span>
                    <div className = 'videocontainer__toolbar'>
                        <StatusIndicators participantID = { id } />
                    </div>
                </>
            );
        }

        const style = { background: '#000' };

        return (
            <div >
                <div
                    className = 'videocontainer__background'
                    style = { style } />
                <img
                    className = 'sharedVideoAvatar'
                    src = { `https://img.youtube.com/vi/${id}/0.jpg` } />
                <div className = 'videocontainer__toptoolbar'>
                    {this._renderTopIndicators()}
                </div>
                <div className = 'videocontainer__toolbar'>
                    <StatusIndicators participantID = { id } />
                </div>
                <div className = 'videocontainer__hoverOverlay' />
                <div className = 'displayNameMainContainer'>
                    <div className = 'displayNameContainer'>
                        <DisplayName
                            elementID = { `participant_${id}_name` }
                            participantID = { id } />
                    </div>
                </div>
            </div>
        );
    }

    /**
   * Renders the top indicators of the thumbnail.
   *
   * @returns {Component}
   */
    _renderTopIndicators() {
        const {
            _connectionIndicatorAutoHideEnabled,
            _connectionIndicatorDisabled,
            _currentLayout,
            _isDominantSpeakerDisabled,
            _indicatorIconSize: iconSize,
            _participant,
            isHovered
        } = this.props;
        const showConnectionIndicator
            = isHovered || !_connectionIndicatorAutoHideEnabled;
        const { id, local = false, dominantSpeaker = false, pinned, moderatorPin } = _participant;
        const showDominantSpeaker = !_isDominantSpeakerDisabled && dominantSpeaker;
        let statsPopoverPosition;

        switch (_currentLayout) {
        case LAYOUTS.TILE_VIEW:
            statsPopoverPosition = 'right-start';
            break;
        case LAYOUTS.VERTICAL_FILMSTRIP_VIEW:
            statsPopoverPosition = 'right-start';
            break;
        default:
            statsPopoverPosition = 'auto';
        }

        return (
            <div>
                <AtlasKitThemeProvider mode = 'dark'>
                    {!moderatorPin && pinned && (
                        <BaseIndicator
                            className = 'indicator show-inline'
                            icon = { IconUserPinned }
                            iconClassName = 'indicatoricon pipstroke'
                            iconSize = { `${iconSize}px` }
                            tooltipKey = { this.props.t('videothumbnail.userispinned') }
                            tooltipPosition = { 'bottom-left' } />
                    )}
                    {moderatorPin && (
                        <BaseIndicator
                            className = 'indicator show-inline'
                            icon = { IconModeratorPin }
                            iconClassName = 'indicatoricon pipstroke'
                            iconSize = { `${iconSize}px` }
                            tooltipKey = { this.props.t('videothumbnail.pinByModerator') }
                            tooltipPosition = { 'bottom-left' } />
                    )}
                    {!_connectionIndicatorDisabled && (
                        <ConnectionIndicator
                            StatusIcon = { IconNetwork }
                            _connectionStatus = { _participant?.connectionStatus }
                            alwaysVisible = { showConnectionIndicator }
                            enableStatsDisplay = { true }
                            iconSize = { iconSize }
                            isLocalVideo = { local }
                            largeVideo = { false }
                            participantId = { id }
                            statsPopoverPosition = { statsPopoverPosition }
                            tooltipKey = { 'connection indicator' }
                            tooltipPosition = { 'bottom-left' } />
                    )}
                    {/* <RaisedHandIndicator
                        iconSize={iconSize}
                        participantId={id}
                        tooltipPosition={tooltipPosition} /> */}
                    {showDominantSpeaker && (
                        <DominantSpeakerIndicator
                            iconSize = { iconSize }
                            tooltipKey = { this.props.t('videothumbnail.dominentSpeakerTooltip') }
                            tooltipPosition = { 'bottom-left' } />
                    )}
                </AtlasKitThemeProvider>
            </div>
        );
    }

    /**
   * Returns the border styles for the container.
   *
   * @param {number} level - The audio level.
   * @returns {Object} - The border styles.
   */
    getContainerBorderStyles(level) {
        const threshold = 0.15;
        const baseColor = '#4b6790';
        const activeColor = '#4a6b94';

        if (level > threshold) {
            return {
                border: `4px solid ${activeColor}`,
                boxShadow: `0 0 8px ${activeColor}`,
                transition: 'border-color 0.1s ease, box-shadow 0.1s ease',
                animation: 'speakingPulse 1s ease-in-out infinite alternate'
            };
        }

        return {
            border: `4px solid ${baseColor}`,
            boxShadow: 'none',
            transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
            animation: 'none'
        };

    }

    /**
   * Updates the small video shadow effect.
   */
    _updateSmallVideoShadow(level, color) {
        const scale = 2;

        // Internal circle audio level.
        const int = {
            level: level > 0.15 ? 10 : 0,
            color: color || interfaceConfig.AUDIO_LEVEL_PRIMARY_COLOR
        };

        // External circle audio level.
        const ext = {
            level: parseFloat(
                ((int.level * scale * level) + int.level).toFixed(0)),
            color: interfaceConfig.AUDIO_LEVEL_SECONDARY_COLOR
        };

        // Internal blur.
        int.blur = int.level ? 2 : 0;

        // External blur.
        ext.blur = ext.level ? 6 : 0;

        if (color) {
            return [
                `0 0 ${ext.blur}px ${ext.level}px ${color}`
            ].join(', ');
        }

        return [
            `0 0 ${int.blur}px ${int.level}px ${int.color}`,
            `0 0 ${ext.blur}px ${ext.level}px ${ext.color}`
        ].join(', ');

    }

    /**
   * Renders the avatar.
   *
   * @returns {ReactElement}
   */
    _renderAvatar() {
        const { _participant, _isAudioMuted, _audioTrack } = this.props;
        const { id } = _participant;
        const styles = this._getStyles();

        return (
            <div
                className = 'avatar-container'
                style = { styles.avatarContainer }>
                <Avatar
                    audioLevel = { _isAudioMuted || _audioTrack?.muted ? 0 : this.state.audioLevel }
                    className = 'userAvatar'
                    participantId = { id } />
            </div>
        );
    }

    /**
   * Renders GIF.
   *
   * @returns {Component}
   */
    _renderGif() {
        const { _gifSrc } = this.props;

        return (
            _gifSrc && (
                <div className = { 'gif-container' }>
                    <img
                        alt = 'GIF'
                        src = { _gifSrc } />
                </div>
            )
        );
    }

    /**
     * Determines whether the blurred background video should be displayed.
     *
     * @private
     * @returns {boolean}
     */
    _shouldShowBlurBackground() {
        const {
            _currentLayout,
            _isAudioOnly,
            _gifSrc,
            _isVideoMuted,
            _objectFit,
            _participant,
            _videoTrack
        } = this.props;

        const isFakeScreenshare = _participant?.isFakeParticipant === 'LocalScreenShare'
            || _participant?.isFakeParticipant === 'RemoteScreenShare'
            || _participant?.fakeParticipant === 'LocalScreenShare'
            || _participant?.fakeParticipant === 'RemoteScreenShare'
            || _videoTrack?.videoType === 'desktop'
            || _videoTrack?.mediaType === MEDIA_TYPE.SCREENSHARE;

        const effectiveObjectFit = isFakeScreenshare ? 'contain' : _objectFit;

        return _currentLayout === LAYOUTS.TILE_VIEW
            && effectiveObjectFit === 'contain'
            && !_isVideoMuted
            && !_isAudioOnly
            && !_gifSrc
            && Boolean(_videoTrack);
    }

    /**
   * Renders the local participant's thumbnail.
   *
   * @returns {ReactElement}
   */
    _renderLocalParticipant() {
        const {
            _defaultLocalDisplayName,
            _disableProfile,
            _participant,
            _videoTrack,
            _participants,
            _gifSrc,
            _isVideoMuted,
            _filmStripView,
            _showScreenShareIndicator,
            _isPictureInPictureEnabled,
            _isAudioOnly,
            _localFlipX
        } = this.props;
        const { id } = _participant;
        const audioLevel = this.props._isAudioMuted || this.props._audioTrack?.muted ? 0 : this.state.audioLevel;

        const { color } = _participants.find(p => p.id === id);
        const participantIDColor = color;
        const displayNameContainerStyle = {
            position: 'relative',
            display: 'flex'
        };

        const heightStyle = { height: '1000px' };

        return (
            <div
                onClick = { () => this.props.dispatch(setCustomParticipantVisible(false)) }
                style = { heightStyle }>
                <div
                    className = 'videocontainer__background'
                    style = {{ background: participantIDColor }}>
                    {this._shouldShowBlurBackground() && (
                        <ThumbnailVideoBackground
                            isLocal = { true }
                            mirror = { _localFlipX }
                            participantId = { id }
                            videoTrack = { _videoTrack } />
                    )}
                </div>
                <span id = 'localVideoWrapper'>
                    <VideoTrack
                        id = 'localVideo_container'
                        videoTrack = { _videoTrack } />
                </span>
                <div className = 'videocontainer__toolbar'>
                    <StatusIndicators
                        _showScreenShareIndicator = { _showScreenShareIndicator }
                        participantID = { id } />
                </div>
                <div className = 'videocontainer__toptoolbar'>
                    {this._renderTopIndicators()}
                </div>
                <div className = 'videocontainer__hoverOverlay' />
                {this._renderGif()}
                {!_gifSrc && (_isVideoMuted || _isAudioOnly) && this._renderAvatar()}
                <div className = 'displayNameMainContainer'>
                    <div
                        className = 'displayNameContainer'
                        style = { displayNameContainerStyle }>
                        <DisplayName
                            allowEditing = { !_disableProfile }
                            audioLevel = { audioLevel }
                            displayNameSuffix = { _defaultLocalDisplayName }
                            elementID = 'localDisplayName'
                            participantID = { id } />
                    </div>
                </div>
                {!config?.iAmRecorder && !_isPictureInPictureEnabled && <RemoteVideoMenu
                    dispatch = { this.props.dispatch }
                    filmStripView = { _filmStripView }
                    participantID = { id } />}

            </div>
        );
    }

    /**
   * Renders the local participant's thumbnail for Stage View.
   *
   * @returns {ReactElement}
   */
    _renderLocalParticipantStageView() {
        const {
            _defaultLocalDisplayName,
            _disableProfile,
            _participant,
            _videoTrack,
            _participants,
            _gifSrc,
            _isVideoMuted,
            _showScreenShareIndicator,
            _isAudioOnly
        } = this.props;
        const { id } = _participant;
        const { audioLevel } = this.state;

        const { color } = _participants.find(p => p.id === id);
        const participantIDColor = color;

        const displayNameContainerStyle = {
            position: 'relative',
            display: 'flex'
        };

        return (
            <div className = 'stage-view'>
                <div
                    className = 'close-local-participant-stageview' >
                    <Icon src = { IconClose } />
                </div>
                <div>
                    <div
                        className = 'videocontainer__background'
                        style = {{ background: participantIDColor }} />
                    {(!_isVideoMuted && !_isAudioOnly) && (
                        <span id = 'localVideoWrapper'>
                            <VideoTrack
                                id = 'localVideo_container'
                                videoTrack = { _videoTrack } />
                        </span>
                    )}
                    <div className = 'videocontainer__toolbar'>
                        <StatusIndicators
                            _showScreenShareIndicator = { _showScreenShareIndicator }
                            participantID = { id } />
                    </div>
                    <div className = 'videocontainer__toptoolbar'>
                        {this._renderTopIndicators()}
                    </div>
                    <div className = 'videocontainer__hoverOverlay' />
                    {this._renderGif()}
                    {!_gifSrc && (_isVideoMuted || _isAudioOnly) && this._renderAvatar()}
                    <div className = 'displayNameMainContainer'>
                        <div
                            className = 'displayNameContainer'
                            style = { displayNameContainerStyle }>
                            <DisplayName
                                allowEditing = { !_disableProfile }
                                audioLevel = { audioLevel }
                                displayNameSuffix = { _defaultLocalDisplayName }
                                elementID = 'localDisplayName'
                                participantID = { id } />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /**
   * Renders a remote participant's 'thumbnail.
   *
   * @returns {ReactElement}
   */
    _renderRemoteParticipant() {
        const {
            _audioTrack,
            _participant,
            _startSilent,
            _isPictureInPictureEnabled,
            _gifSrc,
            _participants,
            _screenshareOwnerId,
            _filmStripView,
            _tempMuted,
            _videoTrack
        } = this.props;
        const { id } = _participant;
        const audioLevel = this.props._isAudioMuted || this.props._audioTrack?.muted ? 0 : this.state.audioLevel;
        const { color } = _participants.find(p => p.id === (_screenshareOwnerId || id)) || {};

        const mhTrack = _audioTrack?.mhTrack;
        const audioTrackId = mhTrack && mhTrack.getId();
        const shouldShowToolbar = _participant?.isFakeParticipant !== 'LocalScreenShare' && _participant?.isFakeParticipant !== 'RemoteScreenShare';

        const participantIDColor = color;

        const heightStyle = { height: '1000px' };

        return (
            <div
                onClick = { () => this.props.dispatch(setCustomParticipantVisible(false)) }
                style = { heightStyle }>
                {_audioTrack ? (
                    <AudioTrack
                        audioTrack = { _audioTrack }
                        id = { `remoteAudio_${audioTrackId || ''}` }
                        muted = { _startSilent }
                        onInitialVolumeSet = { this._onInitialVolumeSet }
                        volume = { _tempMuted ? 0.1 : parseInt(_participant?.volume, 10) / 100 } />
                ) : null}
                <div
                    className = 'videocontainer__background'
                    style = {{ background: participantIDColor }} >
                    {this._shouldShowBlurBackground() && (
                        <ThumbnailVideoBackground
                            isLocal = { false }
                            participantId = { id }
                            videoTrack = { _videoTrack } />
                    )}
                    {_participant?.isFakeParticipant === 'RemoteScreenShare' && _filmStripView !== LAYOUTS.TILE_VIEW && (<div className = 'overlay-thumbnail'>
                        <div className = 'overlay-text'>{this.props.t('dialog.screenSharingUser', {
                            displayName: _participant?.name
                        })}</div>
                    </div>)}
                </div>

                <div className = 'videocontainer__toptoolbar'>
                    {this._renderTopIndicators()}
                </div>
                <ConnectionLostOverlay
                    _participant = { _participant }
                    filmStripView = { _filmStripView } />
                {shouldShowToolbar && <div className = 'videocontainer__toolbar'>
                    <StatusIndicators participantID = { id } />
                </div>}
                <div className = 'videocontainer__hoverOverlay' />
                {/* <ConnectionLostOverlay
                    _participant = { _participant }
                    filmStripView = { _filmStripView } /> */}
                {this._renderGif()}
                {!_gifSrc && this._renderAvatar()}
                {(_participant?.isFakeParticipant !== 'RemoteScreenShare' || _filmStripView === LAYOUTS.TILE_VIEW) && <div className = 'displayNameMainContainer'>
                    <div className = 'displayNameContainer'>
                        <DisplayName
                            audioLevel = { audioLevel }
                            elementID = { `participant_${id}_name` }
                            participantID = { id } />
                    </div>
                </div>}

                <div className = 'presence-label-container'>
                    <PresenceLabel
                        className = 'presence-label'
                        participantID = { id } />
                </div>
                {!config?.iAmRecorder && !_isPictureInPictureEnabled && <RemoteVideoMenu
                    dispatch = { this.props.dispatch }
                    filmStripView = { _filmStripView }
                    participantID = { id } />}
            </div>
        );
    }

    _onInitialVolumeSet: (Object) => void;

    /**
   * A handler for the initial volume value of the audio element.
   *
   * @param {number} volume - Properties of the audio element.
   * @returns {void}
   */
    _onInitialVolumeSet(volume) {
        if (this.state.volume !== volume) {
            this.setState({ volume });
        }
    }

    _onVolumeChange: (number) => void;

    /**
   * Handles volume changes.
   *
   * @param {number} value - The new value for the volume.
   * @returns {void}
   */
    _onVolumeChange(value) {
        this.setState({ volume: value });
        participantVolumeChanged(this.props.participantID, value);
    }

    /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {ReactElement}
   */
    render() {
        const { _participant, singlevideo, hideLocalParticipantInStageView } = this.props;

        // Handle undefined participant during room switching
        if (!_participant) {
            return null;
        }

        if (singlevideo && !hideLocalParticipantInStageView && !config?.iAmRecorder) {
            return this._renderLocalParticipantStageView();
        }

        const { isFakeParticipant, local } = _participant;


        if (local && !isFakeParticipant) {
            return this._renderLocalParticipant();
        }

        if (isFakeParticipant && (isFakeParticipant !== 'LocalScreenShare' && isFakeParticipant !== 'RemoteScreenShare')) {
            return this._renderFakeParticipant();
        }

        return this._renderRemoteParticipant();
    }
}

/**
 * Maps (parts of) the redux state to the associated props for this component.
 *
 * @param {Object} state - The Redux state.
 * @param {Object} ownProps - The own props of the component.
 * @private
 * @returns {Props}
 */
function _mapStateToProps(state, ownProps): Object {
    const { participantID } = ownProps;


    // Only the local participant won't have id for the time when the conference is not yet joined.
    const participant = participantID
        ? getParticipantById(state, participantID)
        : getLocalParticipant(state);
    const initialVolume = participant?.volume;
    const isLocal = participant?.local;
    const isFakeScreenshare = participant?.isFakeParticipant === 'LocalScreenShare'
        || participant?.isFakeParticipant === 'RemoteScreenShare'
        || participant?.fakeParticipant === 'LocalScreenShare'
        || participant?.fakeParticipant === 'RemoteScreenShare';


    // For fake screenshare participants, get the desktop track
    let _videoTrack;
    let _audioTrack;

    if (isFakeScreenshare) {
        // Get the desktop/screenshare track for this fake participant
        const tracks = state['features/base/tracks'];

        if (participant?.isFakeParticipant === 'LocalScreenShare'
            || participant?.fakeParticipant === 'LocalScreenShare'
            || isLocal) {
            _videoTrack = getLocalDesktopTrack(tracks);
        } else {
            _videoTrack = getVirtualScreenshareParticipantTrack(tracks, participantID)
                || tracks.find(
                    t => (t.videoType === 'desktop' || t.mediaType === MEDIA_TYPE.SCREENSHARE)
                        && t?.participantId === getVirtualScreenshareParticipantOwnerId(participantID)
                        && !t.muted
                );
        }
        _audioTrack = null; // Screenshare typically doesn't have audio
    } else {
        _videoTrack = isLocal
            ? getLocalVideoTrack(state['features/base/tracks'])
            : getTrackByMediaTypeAndParticipant(
                state['features/base/tracks'],
                MEDIA_TYPE.VIDEO,
                participantID
            );
        _audioTrack = isLocal
            ? getLocalAudioTrack(state['features/base/tracks'])
            : getTrackByMediaTypeAndParticipant(
                state['features/base/tracks'],
                MEDIA_TYPE.AUDIO,
                participantID
            );
    }
    const tracks = state['features/base/tracks'];
    const screenShareTrack = participantID
        ? getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.SCREENSHARE, participantID)
            || getScreenShareTrack(tracks, participantID)
        : undefined;
    let isScreenSharing = false;
    const isVideoMuted = isFakeScreenshare
        ? !_videoTrack || Boolean(_videoTrack.muted)
        : participant?.local ? isLocalTrackMuted(tracks, MEDIA_TYPE.VIDEO) : isRemoteTrackMuted(tracks, MEDIA_TYPE.VIDEO, participantID);
    const isAudioMuted = participant?.local ? isLocalTrackMuted(tracks, MEDIA_TYPE.AUDIO) : isRemoteTrackMuted(tracks, MEDIA_TYPE.AUDIO, participantID);

    isScreenSharing = Boolean(screenShareTrack && !screenShareTrack.muted);

    const _participants = state['features/base/participants'];
    const _currentLayout = getCurrentLayout(state);
    let size = {};
    const { startSilent, disableProfile = false } = state['features/base/config'];
    const { NORMAL = 8 } = interfaceConfig.INDICATOR_FONT_SIZES || {};
    const { filmStripView, objectFit } = state['features/base/conference'];
    const { localFlipX } = state['features/base/settings'];
    const screensharetrack = tracks.filter(t => t.videoType === 'desktop');

    switch (_currentLayout) {
    case LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW: {
        const {
            horizontalViewDimensions = {
                local: {},
                remote: {}
            }
        } = state['features/filmstrip'];
        const { local, remote } = horizontalViewDimensions;
        const { width, height } = isLocal ? local : remote;

        size = {
            _width: width,
            _height: height
        };

        break;
    }
    case LAYOUTS.VERTICAL_FILMSTRIP_VIEW:
        size = {
            _heightToWidthPercent: isLocal
                ? 100 / interfaceConfig.LOCAL_THUMBNAIL_RATIO
                : 100 / interfaceConfig.REMOTE_THUMBNAIL_RATIO
        };
        break;
    case LAYOUTS.TILE_VIEW: {
        const { width, height }
                = state['features/filmstrip'].tileViewDimensions.thumbnailSize;

        size = {
            _width: width,
            _height: height
        };
        break;
    }
    }

    const { gifUrl: gifSrc } = getGifForParticipant(state, participant?.id ?? '');
    const mode = getGifDisplayMode(state);
    const remoteScreenShare = state['features/video-layout'].remoteScreenShares;
    const remoteScreenShareId = remoteScreenShare.includes(participantID);
    const hideLocalParticipantInStageView = state['features/filmstrip'].hideLocal;
    const tempMuted = state['features/base/conference'].tempMuted;
    const _isAudioOnly = state['features/base/audio-only']?.enabled || false;

    return {
        _audioTrack,
        _isAudioMuted: isAudioMuted,
        _connectionIndicatorAutoHideEnabled:
            interfaceConfig.CONNECTION_INDICATOR_AUTO_HIDE_ENABLED,
        _connectionIndicatorDisabled: interfaceConfig.CONNECTION_INDICATOR_DISABLED,
        _currentLayout,
        _defaultLocalDisplayName: interfaceConfig.DEFAULT_LOCAL_DISPLAY_NAME,
        _disableProfile: disableProfile,
        _isDominantSpeakerDisabled:
            interfaceConfig.DISABLE_DOMINANT_SPEAKER_INDICATOR,
        _indicatorIconSize: NORMAL,
        _participant: participant,
        _participantCount: getParticipantCount(state),
        _sharedVideoState: state['features/shared-video'],
        _startSilent: Boolean(startSilent),
        _videoTrack,
        _gifSrc: mode === 'chat' ? null : gifSrc,
        _tileViewEnabled: shouldDisplayTileView(state),
        _initialVolume: initialVolume,
        _screenshareOwnerId: participant?.isFakeParticipant && getScreenSharingParticipantId(participant?.id),
        _participants,
        _filmStripView: filmStripView,
        _objectFit: isFakeScreenshare ? 'contain' : objectFit || 'cover',
        _localFlipX: Boolean(localFlipX),
        remoteScreenShareId,
        _showScreenShareIndicator: isScreenSharing,
        _screenSharingParticipantId: screensharetrack[0]?.participantId || getScreenshareParticipantIds(state)[0],
        hideLocalParticipantInStageView,
        _isVideoMuted: isVideoMuted,
        _tempMuted: tempMuted,
        _isAudioOnly,
        _isPictureInPictureEnabled: getPictureInPictureModeConfig(state),
        ...size
    };
}

export default connect(_mapStateToProps)(translate(Thumbnail));
